/**
 * Ertrag und Wirtschaftlichkeit (Kapitel K).
 *
 * Der im Gespraech am deutlichsten formulierte Wunsch. Zwei Dinge sind hier
 * bewusst so gebaut, wie sie gebaut sind:
 *
 * Der Rechenweg steht sichtbar unter dem Ergebnis (PV-K10). Ein
 * Wettbewerbsprodukt wurde kritisiert, weil es bei der Amortisation sehr
 * optimistisch rechnet — Nachvollziehbarkeit ist hier ein Verkaufsargument,
 * kein Beiwerk.
 *
 * Und was fehlt, steht oben. Ohne Strompreis gibt es keine Amortisation,
 * sondern einen Hinweis. Geraten wird nichts.
 */

import { useState } from 'react';

import { neueId, speichere } from '../daten/db.ts';
import { holeStrahlungsdaten, type Ertragsdaten } from '../ertragrechnung.ts';
import type { Wirtschaftlichkeitsdaten } from '../typen.ts';
import { Zahlfeld } from './Zahlfeld.tsx';

interface Eigenschaften {
  projektId: string;
  standort: { lat: number | null; lng: number | null };
  daten: Wirtschaftlichkeitsdaten | null;
  ertrag: Ertragsdaten;
}

const MONATE = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];

export function Wirtschaftlichkeitspanel(e: Eigenschaften): React.JSX.Element {
  const [laedt, setLaedt] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);

  async function aendere(felder: Partial<Wirtschaftlichkeitsdaten>): Promise<void> {
    if (e.daten) {
      await speichere('wirtschaftlichkeit', e.daten, felder as Record<string, unknown>);
      return;
    }
    const neu: Wirtschaftlichkeitsdaten = {
      id: neueId(),
      seq: null,
      schmutzig: true,
      geaendert_am: new Date().toISOString(),
      geloescht_am: null,
      projekt_id: e.projektId,
      jahresverbrauch_kwh: null,
      monatswerte_kwh: null,
      zusatzverbraucher: [],
      speicher_kwh: null,
      foerderung_euro: null,
      preise: null,
      strahlungsdaten: null,
      ...felder,
    };
    await speichere('wirtschaftlichkeit', neu, {
      projekt_id: neu.projekt_id,
      jahresverbrauch_kwh: neu.jahresverbrauch_kwh,
      monatswerte_kwh: neu.monatswerte_kwh,
      zusatzverbraucher: neu.zusatzverbraucher,
      speicher_kwh: neu.speicher_kwh,
      foerderung_euro: neu.foerderung_euro,
      preise: neu.preise,
      strahlungsdaten: neu.strahlungsdaten,
    });
  }

  /** Strahlungsdaten fuer den Standort holen (PV-B07). Braucht Verbindung. */
  async function ladeStrahlung(): Promise<void> {
    if (e.standort.lat === null || e.standort.lng === null) {
      setMeldung('Erst den Standort setzen — über die Adresssuche oder die erste Fläche.');
      return;
    }
    setLaedt(true);
    setMeldung(null);
    try {
      const daten = await holeStrahlungsdaten(e.standort.lat, e.standort.lng);
      await aendere({ strahlungsdaten: daten });
      setMeldung(`Strahlungsdaten von ${daten.quelle} übernommen.`);
    } catch (fehler) {
      setMeldung(`Strahlungsdaten konnten nicht geladen werden: ${String(fehler)}`);
    } finally {
      setLaedt(false);
    }
  }

  const w = e.ertrag.wirtschaftlichkeit;
  const zusatz = e.daten?.zusatzverbraucher ?? [];

  return (
    <div>
      <h2>Ertrag</h2>

      {e.ertrag.luecken.map((luecke, index) => (
        <div key={index} className="hinweis">
          {luecke}
        </div>
      ))}

      <dl className="zahlenblock">
        <div>
          <dt>Anlagenertrag</dt>
          <dd>{Math.round(e.ertrag.ertrag.jahr).toLocaleString('de-AT')} kWh/Jahr</dd>
        </div>
        <div>
          <dt>Spezifisch</dt>
          <dd>
            {e.ertrag.leistungKwp > 0 ? Math.round(e.ertrag.ertrag.jahr / e.ertrag.leistungKwp) : 0}{' '}
            kWh/kWp
          </dd>
        </div>
      </dl>

      <Monatsbalken
        titel="Ertrag und Verbrauch je Monat"
        ertrag={e.ertrag.ertrag.monate}
        verbrauch={e.ertrag.verbrauchMonate}
      />

      <div className="knopfreihe">
        <button onClick={() => void ladeStrahlung()} disabled={laedt}>
          {laedt ? 'Holt Daten …' : 'Strahlungsdaten für den Standort laden'}
        </button>
      </div>
      {meldung && <div className="hinweis">{meldung}</div>}

      <details style={{ margin: '0.75rem 0', fontSize: '0.85rem' }}>
        <summary>Ertrag je Fläche</summary>
        <ul>
          {e.ertrag.ertrag.jeFlaeche.map((flaeche) => (
            <li key={flaeche.flaecheId}>
              {flaeche.name}: {Math.round(flaeche.jahr).toLocaleString('de-AT')} kWh (
              {Math.round(flaeche.spezifisch)} kWh/kWp)
            </li>
          ))}
        </ul>
      </details>

      {/* --- Verbrauch (PV-K04 bis K06) ---------------------------------- */}
      <h2 style={{ marginTop: '1.5rem' }}>Verbrauch</h2>
      <div className="feld">
        <label htmlFor="jahresverbrauch">Jahresverbrauch in kWh</label>
        <Zahlfeld
          id="jahresverbrauch"
          min={0}
          leerErlaubt
          wert={e.daten?.jahresverbrauch_kwh}
          onWert={(kwh) => void aendere({ jahresverbrauch_kwh: kwh })}
        />
        <p style={{ fontSize: '0.78rem', color: 'var(--grau)', margin: '0.3rem 0 0' }}>
          Aus der Jahresabrechnung des Kunden.
          {e.ertrag.profilIstPlatzhalter &&
            ' Der Tagesverlauf kommt aus einem nachgebildeten H0-Profil — die Form stimmt, die Zahlen sind gerundet.'}
        </p>
      </div>

      <div className="feld">
        <label>Zusatzverbraucher</label>
        <div className="knopfreihe">
          {(
            [
              ['waermepumpe', 'Wärmepumpe'],
              ['elektroauto', 'Elektroauto'],
              ['heizstab', 'Heizstab'],
            ] as const
          ).map(([art, text]) => {
            const vorhanden = zusatz.find((v) => v.art === art);
            return (
              <button
                key={art}
                aria-pressed={!!vorhanden}
                onClick={() =>
                  void aendere({
                    zusatzverbraucher: vorhanden
                      ? zusatz.filter((v) => v.art !== art)
                      : [
                          ...zusatz,
                          art === 'elektroauto'
                            ? { art, jahreskilometer: 15000, verbrauchJe100km: 18 }
                            : { art, jahresverbrauchKwh: art === 'waermepumpe' ? 4000 : 1000 },
                        ],
                  })
                }
              >
                {text}
              </button>
            );
          })}
        </div>

        {zusatz.map((verbraucher) => (
          <div key={verbraucher.art} className="knopfreihe" style={{ marginTop: '0.4rem' }}>
            {verbraucher.art === 'elektroauto' ? (
              <>
                <label className="klein">
                  Jahreskilometer
                  <Zahlfeld
                    min={0}
                    ganz
                    wert={verbraucher.jahreskilometer ?? 15000}
                    onWert={(km) =>
                      km !== null &&
                      void aendere({
                        zusatzverbraucher: zusatz.map((v) =>
                          v.art === 'elektroauto' ? { ...v, jahreskilometer: km } : v,
                        ),
                      })
                    }
                  />
                </label>
                <button
                  aria-pressed={verbraucher.ladenTagsueber === true}
                  onClick={() =>
                    void aendere({
                      zusatzverbraucher: zusatz.map((v) =>
                        v.art === 'elektroauto' ? { ...v, ladenTagsueber: !v.ladenTagsueber } : v,
                      ),
                    })
                  }
                >
                  Lädt tagsüber
                </button>
              </>
            ) : (
              <label className="klein">
                {verbraucher.art === 'waermepumpe' ? 'Wärmepumpe' : 'Heizstab'} kWh/Jahr
                <Zahlfeld
                  min={0}
                  wert={verbraucher.jahresverbrauchKwh ?? 0}
                  onWert={(kwh) =>
                    kwh !== null &&
                    void aendere({
                      zusatzverbraucher: zusatz.map((v) =>
                        v.art === verbraucher.art ? { ...v, jahresverbrauchKwh: kwh } : v,
                      ),
                    })
                  }
                />
              </label>
            )}
          </div>
        ))}
      </div>

      {/* --- Speicher (PV-K08) ------------------------------------------- */}
      <h2 style={{ marginTop: '1.5rem' }}>Speicher</h2>
      <div className="feld">
        <label htmlFor="speicher">Kapazität in kWh</label>
        <Zahlfeld
          id="speicher"
          min={0}
          leerErlaubt
          wert={e.daten?.speicher_kwh}
          onWert={(kwh) => void aendere({ speicher_kwh: kwh })}
        />
      </div>

      {e.ertrag.speichervergleich && (
        <>
          <div className="tabellenrahmen" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th style={kopf}>Speicher</th>
                  <th style={{ ...kopf, textAlign: 'right' }}>Eigenverbrauch</th>
                  <th style={{ ...kopf, textAlign: 'right' }}>Autarkie</th>
                  <th style={{ ...kopf, textAlign: 'right' }}>Amortisation</th>
                </tr>
              </thead>
              <tbody>
                {e.ertrag.speichervergleich.varianten.map((variante) => (
                  <tr
                    key={variante.kapazitaetKwh}
                    style={{
                      background:
                        variante.kapazitaetKwh === e.ertrag.speichervergleich?.vorschlagKwh
                          ? 'var(--flaeche)'
                          : undefined,
                    }}
                  >
                    <td style={zelle}>
                      {variante.kapazitaetKwh === 0 ? 'ohne' : `${variante.kapazitaetKwh} kWh`}
                      {variante.kapazitaetKwh === e.ertrag.speichervergleich?.vorschlagKwh && (
                        <span className="gedaempft"> · Vorschlag</span>
                      )}
                    </td>
                    <td style={{ ...zelle, textAlign: 'right' }}>
                      {Math.round(variante.eigenverbrauchsanteil * 100)} %
                    </td>
                    <td style={{ ...zelle, textAlign: 'right' }}>
                      {Math.round(variante.autarkiegrad * 100)} %
                    </td>
                    <td style={{ ...zelle, textAlign: 'right' }}>
                      {variante.amortisationJahre === null
                        ? '—'
                        : `${variante.amortisationJahre.toFixed(1)} J`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            onClick={() =>
              void aendere({ speicher_kwh: e.ertrag.speichervergleich?.vorschlagKwh ?? null })
            }
            style={{ marginBottom: '1rem' }}
          >
            Vorschlag übernehmen
          </button>
        </>
      )}

      {/* --- Ergebnis (PV-K09) ------------------------------------------- */}
      <h2 style={{ marginTop: '1.5rem' }}>Wirtschaftlichkeit</h2>

      <div className="feld">
        <label htmlFor="foerderung">Förderung in Euro</label>
        <Zahlfeld
          id="foerderung"
          min={0}
          leerErlaubt
          stellen={2}
          wert={e.daten?.foerderung_euro}
          onWert={(euro) => void aendere({ foerderung_euro: euro })}
        />
      </div>

      {w ? (
        <>
          <dl className="zahlenblock">
            <div>
              <dt>Investition</dt>
              <dd>{euro(w.investitionEuro)}</dd>
            </div>
            <div>
              <dt>Amortisation</dt>
              <dd>
                {w.amortisationJahre === null
                  ? 'über 20 Jahre'
                  : `${w.amortisationJahre.toFixed(1)} Jahre`}
              </dd>
            </div>
            <div>
              <dt>Ersparnis Jahr 1</dt>
              <dd>{euro(w.ersparnisErstesJahrEuro)}</dd>
            </div>
            <div>
              <dt>Einspeiseerlös</dt>
              <dd>{euro(w.einspeiseerloesErstesJahrEuro)}</dd>
            </div>
            <div>
              <dt>Eigenverbrauch</dt>
              <dd>{Math.round(w.bilanz.eigenverbrauchsanteil * 100)} %</dd>
            </div>
            <div>
              <dt>Autarkie</dt>
              <dd>{Math.round(w.bilanz.autarkiegrad * 100)} %</dd>
            </div>
          </dl>

          {/* PV-K10: jede Zahl auf ihre Eingangswerte zurueckfuehrbar. */}
          <details open style={{ fontSize: '0.82rem', marginTop: '0.5rem' }}>
            <summary>Wie diese Zahlen zustande kommen</summary>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '0.5rem' }}>
              <tbody>
                {w.rechenweg.map((zeile) => (
                  <tr key={zeile.groesse}>
                    <td style={{ ...zelle, width: '35%' }}>{zeile.groesse}</td>
                    <td style={zelle}>
                      <strong>{zeile.wert}</strong>
                      <span style={{ display: 'block', color: 'var(--grau)', fontSize: '0.75rem' }}>
                        {zeile.herkunft}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </>
      ) : (
        <p style={{ color: 'var(--grau)', fontSize: '0.9rem' }}>
          Sobald Strompreis und Einspeisevergütung hinterlegt sind, steht hier die Rechnung.
        </p>
      )}
    </div>
  );
}

/** Zwei Reihen Balken, ohne Diagrammbibliothek — mehr braucht es nicht. */
function Monatsbalken({
  titel,
  ertrag,
  verbrauch,
}: {
  titel: string;
  ertrag: number[];
  verbrauch: number[];
}): React.JSX.Element {
  const groesster = Math.max(1, ...ertrag, ...verbrauch);
  return (
    <figure style={{ margin: '0.75rem 0' }}>
      <figcaption style={{ fontSize: '0.7rem', color: 'var(--grau)', marginBottom: '0.4rem' }}>
        {titel} — <span style={{ color: 'var(--energie)' }}>■</span> Ertrag{' '}
        <span style={{ color: 'var(--wasser)' }}>■</span> Verbrauch
      </figcaption>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '5rem' }}>
        {MONATE.map((monat, index) => (
          <div key={index} style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1px', height: '4.2rem' }}>
              <div
                title={`${Math.round(ertrag[index] ?? 0)} kWh Ertrag`}
                style={{
                  flex: 1,
                  background: 'var(--energie)',
                  height: `${((ertrag[index] ?? 0) / groesster) * 100}%`,
                }}
              />
              <div
                title={`${Math.round(verbrauch[index] ?? 0)} kWh Verbrauch`}
                style={{
                  flex: 1,
                  background: 'var(--wasser)',
                  height: `${((verbrauch[index] ?? 0) / groesster) * 100}%`,
                }}
              />
            </div>
            <span style={{ fontSize: '0.6rem', color: 'var(--grau)' }}>{monat}</span>
          </div>
        ))}
      </div>
    </figure>
  );
}

// Kopf und Zelle kommen aus dem CSS (th, td); hier bleibt nur die Ausrichtung.
const kopf: React.CSSProperties = {};

const zelle: React.CSSProperties = {};

function euro(betrag: number | null | undefined): string {
  if (betrag == null) return '—';
  return betrag.toLocaleString('de-AT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
}
